/**
 * Business Days Utility
 * Calculates business days (Monday-Friday) excluding weekends
 */

/**
 * Check if a date is a business day (Monday-Friday)
 * @param {Date} date
 * @returns {boolean}
 */
function isBusinessDay(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
}

/**
 * Add N business days to a date, skipping weekends. Preserves the time-of-day.
 * ponytail: weekends only, no holiday calendar — add one if publish dates ever
 * need to skip holidays.
 * @param {Date} date
 * @param {number} days
 * @returns {Date} a new Date `days` business days after the input
 */
export function addBusinessDays(date, days) {
  const result = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result)) remaining -= 1;
  }
  return result;
}

/**
 * Calculate business days between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number} Number of business days
 */
export function getBusinessDaysBetween(startDate, endDate) {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (current < end) {
    if (isBusinessDay(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Get the number of calendar days between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number}
 */
export function getCalendarDaysBetween(startDate, endDate) {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return Math.floor((end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Convert a Firestore timestamp to a Date object
 * @param {Object|Date|string|number} timestamp - Firestore timestamp or other date format
 * @returns {Date|null}
 */
export function toDate(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.toDate) return timestamp.toDate();
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  return null;
}
